import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  Eye,
  MessageCircle,
  AlertCircle,
  User,
  Edit3,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { formatTimeAgo, formatDate, getStatusText, getStatusColor } from '../../utils/helpers';

const WorkflowDashboard = () => {
  const { user } = useAuth();
  const isBO = user.role === 'bo';
  const [activeTab, setActiveTab] = useState(isBO ? 'returned' : 'permits');

  // Get pending workflows for current user role (non-BO only)
  const { data: pendingWorkflows, isLoading } = useQuery(
    'pendingWorkflows',
    async () => {
      const response = await api.get('/workflow/pending');
      return response.data.workflows;
    },
    {
      enabled: !isBO,
      refetchInterval: 30000,
    }
  );

  // Get pending edit requests for current user role (non-BO only)
  const { data: pendingEditRequests, isLoading: editRequestsLoading } = useQuery(
    'pendingEditRequests',
    async () => {
      const response = await api.get('/edit-requests/pending/mine');
      return response.data.editRequests;
    },
    {
      enabled: !isBO,
      refetchInterval: 30000,
    }
  );

  // Get workflow statistics (non-BO only)
  const { data: stats } = useQuery(
    'workflowStats',
    async () => {
      const response = await api.get('/workflow/statistics');
      return response.data.statistics;
    },
    {
      enabled: !isBO,
    }
  );

  // Get returned permits for BO
  const { data: returnedPermitsData, isLoading: returnedLoading } = useQuery(
    'returnedPermits',
    async () => {
      const response = await api.get('/permits?status=Returned&limit=50');
      return response.data;
    },
    {
      enabled: isBO,
      refetchInterval: 30000,
    }
  );

  const loading = isBO ? returnedLoading : (isLoading || editRequestsLoading);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const workflows = pendingWorkflows || [];
  const editRequests = pendingEditRequests || [];
  const returnedPermits = returnedPermitsData?.permits || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Workflow Dashboard</h1>
        <p className="text-secondary-600">
          {user.role === 'bo' && 'Review and resubmit returned permits'}
          {user.role === 'eac' && 'Review and approve permits for SDO approval'}
          {user.role === 'sdo' && 'Review and approve permits for ADC approval'}
          {user.role === 'adc' && 'Review and approve permits for DC approval'}
          {user.role === 'dc' && 'Final approval and processing of permits'}
        </p>
      </div>

      {/* Statistics Cards - Non-BO only */}
      {!isBO && stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-500 truncate">
                      Pending Approvals
                    </dt>
                    <dd className="text-lg font-medium text-secondary-900">
                      {(stats.totalPermits || 0) + (editRequests.length || 0)}
                    </dd>
                    <p className="text-xs text-secondary-500 mt-1">
                      {stats.totalPermits || 0} permits, {editRequests.length || 0} edit requests
                    </p>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-500 truncate">
                      Approved This Month
                    </dt>
                    <dd className="text-lg font-medium text-secondary-900">
                      {stats.approved_permits || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-500 truncate">
                      Average Processing Time
                    </dt>
                    <dd className="text-lg font-medium text-secondary-900">
                      {stats.avg_approval_time_days ? `${Math.round(stats.avg_approval_time_days)} days` : 'N/A'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BO Stats Card */}
      {isBO && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <RotateCcw className="h-8 w-8 text-orange-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-500 truncate">
                      Returned Permits
                    </dt>
                    <dd className="text-lg font-medium text-secondary-900">
                      {returnedPermits.length}
                    </dd>
                    <p className="text-xs text-secondary-500 mt-1">
                      Requires your attention
                    </p>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabbed Interface */}
      <div className="bg-white shadow rounded-lg">
        {/* Tab Headers */}
        <div className="border-b border-secondary-200">
          <nav className="flex -mb-px">
            {isBO ? (
              <button
                onClick={() => setActiveTab('returned')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'returned'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                Returned Permits ({returnedPermits.length})
              </button>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab('permits')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'permits'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                  }`}
                >
                  Pending Reviews ({workflows.length})
                </button>
                <button
                  onClick={() => setActiveTab('editRequests')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'editRequests'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                  }`}
                >
                  Pending Edit Requests ({editRequests.length})
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {/* BO Returned Permits Tab */}
          {isBO && activeTab === 'returned' && (
            <>
              {returnedPermits.length > 0 ? (
                <div className="divide-y divide-secondary-200">
                  {returnedPermits.map((permit) => (
                    <div key={permit.id} className="p-6 hover:bg-secondary-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-secondary-900">
                              {permit.permit_no}
                            </h4>
                            <span className={`status-badge ${getStatusColor(permit.status)}`}>
                              {getStatusText(permit.status)}
                            </span>
                          </div>

                          <div className="mt-2 space-y-1">
                            <p className="text-secondary-700">{permit.name}</p>
                            <p className="text-sm text-secondary-600">{permit.village}</p>
                            <div className="flex items-center text-sm text-secondary-500 space-x-4">
                              <span>Area: {permit.size_sqft} sq ft</span>
                              <span>Created: {formatDate(permit.createdAt)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="ml-6 flex space-x-2">
                          <Link
                            to={`/permits/${permit.id}`}
                            className="btn btn-outline btn-sm"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Link>
                          <Link
                            to={`/permits/${permit.id}/edit`}
                            className="btn btn-primary btn-sm"
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Edit & Resubmit
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-300" />
                  <h3 className="mt-2 text-lg font-medium text-secondary-900">
                    No returned permits
                  </h3>
                  <p className="mt-1 text-secondary-600">
                    All your permits are up to date.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Permits Tab (non-BO) */}
          {!isBO && activeTab === 'permits' && (
            <>
              {workflows.length > 0 ? (
                <div className="divide-y divide-secondary-200">
                  {workflows.map((workflow) => (
                    <div key={workflow.id} className="p-6 hover:bg-secondary-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-secondary-900">
                              {workflow.Permit.permitNumber}
                            </h4>
                            <span className={`status-badge ${getStatusColor(workflow.Permit.status)}`}>
                              {getStatusText(workflow.Permit.status)}
                            </span>
                          </div>

                          <div className="mt-2 space-y-1">
                            <p className="text-secondary-700">{workflow.Permit.name}</p>
                            <p className="text-sm text-secondary-600">{workflow.Permit.address}</p>
                            <div className="flex items-center text-sm text-secondary-500 space-x-4">
                              <span>Area: {workflow.Permit.area} sq m</span>
                              <span>Submitted: {formatTimeAgo(workflow.Permit.submittedAt)}</span>
                              {workflow.Permit.submitter && (
                                <span className="flex items-center">
                                  <User className="h-3 w-3 mr-1" />
                                  {workflow.Permit.submitter.fullName}
                                </span>
                              )}
                            </div>
                          </div>

                          {workflow.comments && (
                            <div className="mt-3 p-3 bg-secondary-100 rounded-md">
                              <p className="text-sm text-secondary-700">
                                <MessageCircle className="h-4 w-4 inline mr-1" />
                                {workflow.comments}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="ml-6">
                          <Link
                            to={`/permits/${workflow.Permit.id}`}
                            className="btn btn-primary btn-sm"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-300" />
                  <h3 className="mt-2 text-lg font-medium text-secondary-900">
                    No pending approvals
                  </h3>
                  <p className="mt-1 text-secondary-600">
                    All permits have been processed. Great work!
                  </p>
                </div>
              )}
            </>
          )}

          {/* Edit Requests Tab (non-BO) */}
          {!isBO && activeTab === 'editRequests' && (
            <>
              {editRequests.length > 0 ? (
                <div className="divide-y divide-secondary-200">
                  {editRequests.map((editRequest) => (
                    <div key={editRequest.id} className="p-6 hover:bg-secondary-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-secondary-900">
                              Edit Request for {editRequest.permit.permit_no}
                            </h4>
                            <span className={`status-badge ${getStatusColor(editRequest.status)}`}>
                              {getStatusText(editRequest.status)}
                            </span>
                          </div>

                          <div className="mt-2 space-y-1">
                            <p className="text-secondary-700">{editRequest.permit.name}</p>
                            <p className="text-sm text-secondary-600">{editRequest.permit.village}</p>
                            <div className="flex items-center text-sm text-secondary-500 space-x-4">
                              <span>Initiated by: {editRequest.initiator?.fullName || 'Unknown'}</span>
                              <span>({editRequest.initiatorRole?.toUpperCase()})</span>
                              <span>Submitted: {formatTimeAgo(editRequest.createdAt)}</span>
                            </div>
                          </div>

                          {editRequest.reason && (
                            <div className="mt-3 p-3 bg-secondary-100 rounded-md">
                              <p className="text-sm text-secondary-700 line-clamp-2">
                                <MessageCircle className="h-4 w-4 inline mr-1" />
                                {editRequest.reason}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="ml-6">
                          <Link
                            to={`/edit-requests/${editRequest.id}`}
                            className="btn btn-primary btn-sm"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-300" />
                  <h3 className="mt-2 text-lg font-medium text-secondary-900">
                    No pending edit requests
                  </h3>
                  <p className="mt-1 text-secondary-600">
                    All edit requests have been processed.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowDashboard;
